import { useCallback, useMemo } from 'react';

import { dayjs } from '~/utils/datetime';
import { date4server } from '~/utils/l10n';

import { DatePicker } from '~/ui/molecules/DateInput';

import {
  type FilterDraftValue,
  type UniversalBuiltInDrawerFieldConfig,
} from '../../types';

type DateDrawerFieldConfig = Extract<
  UniversalBuiltInDrawerFieldConfig,
  { input: 'date' }
>;

type DateDrawerFieldControlProps = {
  readonly fieldConfig: DateDrawerFieldConfig;
  readonly maxDate?: string;
  readonly minDate?: string;
  readonly onChange: (next?: FilterDraftValue) => void;
  readonly testId: string;
  readonly value?: FilterDraftValue;
};

function toDateValue(value?: FilterDraftValue) {
  if (typeof value !== 'string') return undefined;

  const parsed = dayjs(value, date4server, true);

  return parsed.isValid() ? parsed.toDate() : undefined;
}

function toDateDraftValue(value: Date | undefined) {
  return value ? dayjs(value).format(date4server) : undefined;
}

export function DateDrawerFieldControl({
  fieldConfig,
  maxDate,
  minDate,
  value,
  onChange,
  testId,
}: DateDrawerFieldControlProps) {
  const dateValue = useMemo(() => toDateValue(value), [value]);
  const maxDateValue = useMemo(() => toDateValue(maxDate), [maxDate]);
  const minDateValue = useMemo(() => toDateValue(minDate), [minDate]);
  const handleChange = useCallback(
    (nextDate: Date | undefined) => {
      const next = toDateDraftValue(nextDate);

      if (next !== value) {
        onChange(next);
      }
    },
    [onChange, value],
  );

  return (
    <div data-testid={testId}>
      <DatePicker
        maxDate={maxDateValue}
        minDate={minDateValue}
        textFieldProps={{
          className: 'bg-white',
          fullWidth: true,
          placeholder: fieldConfig.placeholder,
          size: 'small',
        }}
        value={dateValue}
        onChange={handleChange}
      />
    </div>
  );
}
